/**
 * Rate Limiter Utility
 * Prevents rapid-fire form submissions from the same user
 * Uses in-memory storage (localStorage for persistence across page reloads)
 */

class RateLimiter {
    constructor() {
        this.attempts = new Map(); // In-memory storage for current session
        this.STORAGE_KEY = 'portfolio_rate_limit';
    }

    /**
     * Get submission history from localStorage
     */
    getStoredAttempts() {
        try {
            const stored = localStorage.getItem(this.STORAGE_KEY);
            if (stored) {
                return JSON.parse(stored);
            }
        } catch (error) {
            console.error('Error reading rate limit data:', error);
        }
        return {};
    }

    /**
     * Save submission history to localStorage
     */
    saveAttempts(data) {
        try {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
        } catch (error) {
            console.error('Error saving rate limit data:', error);
        }
    }

    /**
     * Generate a key for tracking (email-based)
     */
    generateKey(email, formType = 'contact') {
        return `${formType}:${email.toLowerCase().trim()}`;
    }

    /**
     * Check if submission is allowed
     * @param {string} email - User's email address
     * @param {string} formType - Type of form ('contact', 'newsletter', 'review')
     * @param {object} options - Rate limiting options
     * @returns {object} - { allowed: boolean, timeRemaining: number, message: string }
     */
    checkLimit(email, formType = 'contact', options = {}) {
        const defaults = {
            maxAttempts: 3, // Maximum attempts allowed
            windowMinutes: 60, // Time window in minutes
            cooldownMinutes: 15 // Cooldown after max attempts
        };

        const config = { ...defaults, ...options };
        const key = this.generateKey(email, formType);
        const now = Date.now();
        
        // Get stored attempts
        const stored = this.getStoredAttempts();
        let attemptData = stored[key] || { count: 0, firstAttempt: now, lastAttempt: now };

        // Check if we're in cooldown period
        const timeSinceLastAttempt = now - attemptData.lastAttempt;
        const cooldownMs = config.cooldownMinutes * 60 * 1000;
        
        if (attemptData.count >= config.maxAttempts && timeSinceLastAttempt < cooldownMs) {
            const remainingMs = cooldownMs - timeSinceLastAttempt;
            const remainingMinutes = Math.ceil(remainingMs / 60000);
            
            return {
                allowed: false,
                timeRemaining: remainingMinutes,
                message: `Too many attempts. Please try again in ${remainingMinutes} minute${remainingMinutes > 1 ? 's' : ''}.`,
                retryAfter: new Date(now + remainingMs)
            };
        }

        // Check if time window has expired (reset counter)
        const windowMs = config.windowMinutes * 60 * 1000;
        const timeSinceFirstAttempt = now - attemptData.firstAttempt;
        
        if (timeSinceFirstAttempt > windowMs) {
            // Reset the counter
            attemptData = { count: 0, firstAttempt: now, lastAttempt: now };
        }

        // Check if we've exceeded max attempts within the window
        if (attemptData.count >= config.maxAttempts) {
            return {
                allowed: false,
                timeRemaining: config.cooldownMinutes,
                message: `Too many attempts. Please try again in ${config.cooldownMinutes} minutes.`,
                retryAfter: new Date(now + cooldownMs)
            };
        }

        // Submission is allowed
        return {
            allowed: true,
            attemptsRemaining: config.maxAttempts - attemptData.count - 1,
            message: 'Submission allowed'
        };
    }

    /**
     * Record a submission attempt
     * @param {string} email - User's email address
     * @param {string} formType - Type of form
     */
    recordAttempt(email, formType = 'contact') {
        const key = this.generateKey(email, formType);
        const now = Date.now();
        
        // Get stored attempts
        const stored = this.getStoredAttempts();
        let attemptData = stored[key] || { count: 0, firstAttempt: now, lastAttempt: now };

        // Check if time window has expired
        const windowMs = 60 * 60 * 1000; // 1 hour
        const timeSinceFirstAttempt = now - attemptData.firstAttempt;
        
        if (timeSinceFirstAttempt > windowMs) {
            // Reset the counter
            attemptData = { count: 1, firstAttempt: now, lastAttempt: now };
        } else {
            // Increment counter
            attemptData.count += 1;
            attemptData.lastAttempt = now;
        }

        // Save to storage
        stored[key] = attemptData;
        this.saveAttempts(stored);

        return attemptData;
    }

    /**
     * Clear rate limit for a specific email/form type
     * @param {string} email - User's email address
     * @param {string} formType - Type of form
     */
    clearLimit(email, formType = 'contact') {
        const key = this.generateKey(email, formType);
        const stored = this.getStoredAttempts();
        delete stored[key];
        this.saveAttempts(stored);
    }

    /**
     * Clean up old entries (entries older than 24 hours)
     */
    cleanup() {
        const stored = this.getStoredAttempts();
        const now = Date.now();
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours
        let cleaned = false;

        Object.keys(stored).forEach(key => {
            const attemptData = stored[key];
            if (now - attemptData.lastAttempt > maxAge) {
                delete stored[key];
                cleaned = true;
            }
        });

        if (cleaned) {
            this.saveAttempts(stored);
        }
    }

    /**
     * Get current status for debugging
     */
    getStatus(email, formType = 'contact') {
        const key = this.generateKey(email, formType);
        const stored = this.getStoredAttempts();
        return stored[key] || null;
    }
}

// Export singleton instance
const rateLimiter = new RateLimiter();

// Run cleanup on page load
rateLimiter.cleanup();

export default rateLimiter;

// Export rate limit configurations for different forms
export const RATE_LIMIT_CONFIG = {
    contact: {
        maxAttempts: 3,
        windowMinutes: 60,
        cooldownMinutes: 15
    },
    newsletter: {
        maxAttempts: 2,
        windowMinutes: 60,
        cooldownMinutes: 30
    },
    review: {
        maxAttempts: 2,
        windowMinutes: 120,
        cooldownMinutes: 60
    },
    hire: {
        maxAttempts: 2,
        windowMinutes: 60,
        cooldownMinutes: 30
    }
};
